<template>
  <IconField>
    <i
      v-if="searchValue"
      class="pi pi-filter-fill absolute top-2/4 -mt-2 left-3 text-surface-400 dark:text-surface-600"
    />
    <i
      v-else
      class="pi pi-filter absolute top-2/4 -mt-2 left-3 text-surface-400 dark:text-surface-600"
    />
    <InputText v-model="searchValue" :placeholder="t('placeholder.quickfilter')" />
    <InputIcon
      v-if="searchValue"
      class="mdi mdi-close absolute top-1/2 right-3 cursor-pointer"
      @click="clearFilter"
    />
  </IconField>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useTableFilterStateStore } from '@/stores/tableFilter';

const props = defineProps<{
  filterString: string;
  dataKey: string;
}>();

const { t } = useI18n();
const filterStore = useTableFilterStateStore();

const searchValue = ref<string | null>(props.filterString || null);
let debounceTimeout: ReturnType<typeof setTimeout> | null = null;

const emit = defineEmits<{
  (event: 'update:filterString', value: string): void;
}>();

const clearFilter = () => {
  searchValue.value = '';
  emit('update:filterString', '');
};

watch(
  () => props.filterString,
  (newFilterString) => {
    if (newFilterString && !searchValue.value) {
      searchValue.value = newFilterString;
    }
  },
  { immediate: true },
);

watch(searchValue, (newValue) => {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
  }

  debounceTimeout = setTimeout(() => {
    const filterValue = newValue || '';
    emit('update:filterString', filterValue);

    // Save to store
    if (props.dataKey) {
      filterStore.setFilterString(props.dataKey, filterValue);
    }
  }, 1000);
});

onMounted(() => {
  // Load saved filter string from store
  if (props.dataKey) {
    const savedFilter = filterStore.getFilterString(props.dataKey);
    if (savedFilter && !searchValue.value) {
      searchValue.value = savedFilter;
      emit('update:filterString', savedFilter);
    }
  }
});
</script>
